import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { IconFilter, IconRefresh, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { ActiveFilters, FilterModal } from "../../components/cases";
import { useInfinitePathologistReviewQueue } from "../../hooks/useWorkflows";
import type { CaseFilters } from "../../services";
import type {
  PathologistReviewQueueCase,
  QueueColumnDef,
} from "./queueColumnConfig";
import {
  advanceReviewSorts,
  buildAllPathologistReviewQueueFilters,
  buildSentOutPathologistReviewQueueFilters,
  hasActiveCaseFilters,
  type QueueSortState,
} from "./pathologistReviewQueueView";
import { QueueCaseTable, type QueuePagination } from "./QueueCaseTable";
import { PathologistQueueColorLegend } from "./PathologistQueueColorLegend";
import type { PathologistQueueTabView } from "./pathologistQueueViewPreference";
import {
  applyPathologistQueueDefault,
  createPathologistQueueDefault,
  decodePathologistQueueDefault,
} from "./pathologistQueueDefaultPreference";
import { usePathologistQueueDefaultPreference } from "../../hooks/usePathologistQueueDefaultPreference";

interface AllPathologistCasesTabProps {
  tab?: "all" | "sentOut";
  active: boolean;
  hydrated: boolean;
  invalid: boolean;
  view: PathologistQueueTabView;
  onViewChange: (view: PathologistQueueTabView) => void;
  onClearView: () => void;
  columns: QueueColumnDef[];
  prefixOptions: string[];
  prefixOptionsError?: boolean;
  overdueThresholdDays: number;
  renderHeader: (
    columns: QueueColumnDef[],
    sortState: QueueSortState | QueueSortState[] | null,
    onSort: (columnId: string) => void
  ) => ReactNode;
  renderRow: (
    caseItem: PathologistReviewQueueCase,
    blocked: boolean,
    columns: QueueColumnDef[]
  ) => ReactNode;
}

export function AllPathologistCasesTab({
  tab = "all",
  active,
  hydrated,
  invalid,
  view,
  onViewChange,
  onClearView,
  columns,
  prefixOptions,
  prefixOptionsError = false,
  overdueThresholdDays,
  renderHeader,
  renderRow,
}: AllPathologistCasesTabProps) {
  const filters = view.filters;
  const [debouncedSearch] = useDebouncedValue(filters.search, 300);
  const sorts = useMemo(
    () => view.sorts ?? (view.sort ? [view.sort] : []),
    [view.sort, view.sorts]
  );
  const [defaultMessage, setDefaultMessage] = useState<string | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [defaultSaving, setDefaultSaving] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const blockedObserverRef = useRef<IntersectionObserver | null>(null);
  const { getRawDefault, hydratedTabs, errorTabs, saveDefault } =
    usePathologistQueueDefaultPreference();
  const defaultHydrated = hydratedTabs[tab];
  const defaultLoadError = errorTabs[tab];
  const rawDefault = getRawDefault(tab);
  const savedDefault = useMemo(
    () => decodePathologistQueueDefault(rawDefault, prefixOptions, tab),
    [rawDefault, prefixOptions, tab]
  );
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const backendFilters = useMemo(
    () => ({
      ...(tab === "sentOut"
        ? buildSentOutPathologistReviewQueueFilters(filters)
        : buildAllPathologistReviewQueueFilters(filters)),
      caseType: filters.caseType,
      tatMinDays: filters.tatMinDays,
      tatOverdue: filters.tatOverdue,
      overdueThresholdDays,
      hasPendingTests: filters.hasPendingTests,
      search: debouncedSearch,
      sorts,
    }),
    [debouncedSearch, filters, overdueThresholdDays, sorts, tab]
  );
  const {
    data: activePages,
    isLoading: activeLoading,
    isFetching: activeFetching,
    isFetchingNextPage: activeFetchingNextPage,
    isError: activeError,
    hasNextPage: hasMoreCases,
    fetchNextPage: fetchMoreCases,
    refetch: refetchActive,
  } = useInfinitePathologistReviewQueue(
    { ...backendFilters, isBlocked: false },
    { enabled: active && hydrated && !invalid && filters.isBlocked !== true }
  );
  const {
    data: blockedPages,
    isLoading: blockedLoading,
    isFetching: blockedFetching,
    isFetchingNextPage: blockedFetchingNextPage,
    isError: blockedError,
    hasNextPage: hasMoreBlockedCases,
    fetchNextPage: fetchMoreBlockedCases,
    refetch: refetchBlocked,
  } = useInfinitePathologistReviewQueue(
    { ...backendFilters, isBlocked: true },
    { enabled: active && hydrated && !invalid && filters.isBlocked !== false }
  );
  const activeCases = useMemo(
    () =>
      filters.isBlocked === true
        ? []
        : activePages?.pages.flatMap((result) => result.items) ?? [],
    [activePages, filters.isBlocked]
  );
  const blockedCases = useMemo(
    () =>
      filters.isBlocked === false
        ? []
        : blockedPages?.pages.flatMap((result) => result.items) ?? [],
    [blockedPages, filters.isBlocked]
  );
  const activeTotal = useMemo(
    () => (filters.isBlocked === true ? 0 : activePages?.pages[0]?.total ?? 0),
    [activePages, filters.isBlocked]
  );
  const blockedTotal = useMemo(
    () =>
      filters.isBlocked === false ? 0 : blockedPages?.pages[0]?.total ?? 0,
    [blockedPages, filters.isBlocked]
  );
  const totalCases = useMemo(
    () => activeTotal + blockedTotal,
    [activeTotal, blockedTotal]
  );
  const isFetching = activeFetching || blockedFetching;
  const activePagination = useMemo<QueuePagination>(
    () => ({ page: 1, totalPages: 1, cases: activeCases }),
    [activeCases]
  );
  const blockedPagination = useMemo<QueuePagination>(
    () => ({ page: 1, totalPages: 1, cases: blockedCases }),
    [blockedCases]
  );
  const activeFilters = useMemo(() => hasActiveCaseFilters(filters), [filters]);

  const handleSort = useCallback(
    (columnId: string) => {
      onViewChange({
        ...view,
        sort: null,
        sorts: advanceReviewSorts(sorts, columnId),
        page: 1,
        blockedPage: 1,
      });
    },
    [onViewChange, sorts, view]
  );

  const handlePrefixKeysChange = useCallback(
    (selectedKeys: string[]) => {
      const unavailableKeys = (filters.prefixKeys ?? []).filter(
        (key) => !prefixOptions.includes(key)
      );
      const prefixKeys = Array.from(
        new Set([...unavailableKeys, ...selectedKeys])
      );
      onViewChange({
        ...view,
        filters: {
          ...filters,
          prefixKeys: prefixKeys.length ? prefixKeys : undefined,
        },
        page: 1,
        blockedPage: 1,
      });
    },
    [filters, onViewChange, prefixOptions, view]
  );

  const handleSaveDefault = useCallback(async () => {
    setDefaultMessage(null);
    setDefaultError(null);
    const snapshot = createPathologistQueueDefault(view, prefixOptions, tab);
    if (!snapshot) {
      setDefaultError(
        "This view cannot be saved until its prefix choices are available and valid."
      );
      return;
    }
    setDefaultSaving(true);
    try {
      await saveDefault(tab, snapshot);
      setDefaultMessage("Default saved. Your current view has not changed.");
    } catch {
      setDefaultError("The default could not be saved. Please try again.");
    } finally {
      setDefaultSaving(false);
    }
  }, [prefixOptions, saveDefault, tab, view]);

  const handleApplyDefault = useCallback(() => {
    setDefaultMessage(null);
    setDefaultError(null);
    if (!savedDefault) {
      setDefaultError(
        "The saved default is unavailable or no longer valid for this queue."
      );
      return;
    }
    onViewChange(applyPathologistQueueDefault(view, savedDefault, tab));
    setDefaultMessage("Default applied to your current view.");
  }, [onViewChange, savedDefault, tab, view]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onViewChange({
        ...view,
        filters: {
          ...filters,
          search: event.currentTarget.value || undefined,
        },
        page: 1,
        blockedPage: 1,
      });
    },
    [filters, onViewChange, view]
  );

  const handleApplyFilters = useCallback(
    (nextFilters: CaseFilters) => {
      onViewChange({ ...view, filters: nextFilters, page: 1, blockedPage: 1 });
    },
    [onViewChange, view]
  );

  const handleClearFilters = useCallback(() => {
    onClearView();
  }, [onClearView]);

  const handleActiveFiltersChange = useCallback(
    (nextFilters: CaseFilters) => {
      onViewChange({ ...view, filters: nextFilters, page: 1, blockedPage: 1 });
    },
    [onViewChange, view]
  );

  const handleOpenFilters = useCallback(
    () => openFilterModal(),
    [openFilterModal]
  );
  const handleCloseFilters = useCallback(
    () => closeFilterModal(),
    [closeFilterModal]
  );
  const handleRefresh = useCallback(() => {
    if (filters.isBlocked !== true) void refetchActive();
    if (filters.isBlocked !== false) void refetchBlocked();
  }, [filters.isBlocked, refetchActive, refetchBlocked]);
  const handleRevealMoreCases = useCallback(() => {
    if (hasMoreCases && !activeFetchingNextPage) void fetchMoreCases();
  }, [hasMoreCases, activeFetchingNextPage, fetchMoreCases]);
  const handleRevealMoreBlockedCases = useCallback(() => {
    if (hasMoreBlockedCases && !blockedFetchingNextPage) {
      void fetchMoreBlockedCases();
    }
  }, [hasMoreBlockedCases, blockedFetchingNextPage, fetchMoreBlockedCases]);
  const attachLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (
        !node ||
        !active ||
        !hasMoreCases ||
        activeFetchingNextPage ||
        activeError
      )
        return;
      if (typeof IntersectionObserver === "undefined") return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      observerRef.current = observer;
      observer.observe(node);
    },
    [
      active,
      handleRevealMoreCases,
      hasMoreCases,
      activeFetchingNextPage,
      activeError,
    ]
  );
  const attachBlockedLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      blockedObserverRef.current?.disconnect();
      if (
        !node ||
        !active ||
        !hasMoreBlockedCases ||
        blockedFetchingNextPage ||
        blockedError
      )
        return;
      if (typeof IntersectionObserver === "undefined") return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreBlockedCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      blockedObserverRef.current = observer;
      observer.observe(node);
    },
    [
      active,
      handleRevealMoreBlockedCases,
      hasMoreBlockedCases,
      blockedFetchingNextPage,
      blockedError,
    ]
  );

  return (
    <Tabs.Panel value={tab} pt="sm">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Text fw={600} size="lg">
              {tab === "sentOut" ? "Sent Out" : "All Review Cases"}
            </Text>
            {totalCases > 0 && (
              <Badge size="sm" variant="light" color="blue">
                {totalCases}
              </Badge>
            )}
          </Group>
          <Button
            variant="subtle"
            size="sm"
            onClick={handleRefresh}
            loading={isFetching}
            leftSection={<IconRefresh size={16} />}
          >
            Refresh
          </Button>
        </Group>

        <PathologistQueueColorLegend />

        <Group align="flex-start" grow>
          <Paper p="xs" withBorder style={{ flex: 2 }}>
            <Group gap="xs">
              <IconSearch size={18} />
              <TextInput
                placeholder="Search by case # or patient name"
                value={filters.search || ""}
                onChange={handleSearchChange}
                style={{ flex: 1 }}
                size="sm"
              />
            </Group>
          </Paper>
          <Paper p="xs" withBorder style={{ flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <>
                <Button
                  size="sm"
                  variant="light"
                  onClick={handleOpenFilters}
                  leftSection={<IconFilter size={14} />}
                >
                  {activeFilters ? "Edit Filters" : "Filters"}
                </Button>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={handleSaveDefault}
                  loading={defaultSaving}
                  disabled={!hydrated || !defaultHydrated || defaultLoadError}
                >
                  Save Default
                </Button>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={handleApplyDefault}
                  disabled={!hydrated || !defaultHydrated || !savedDefault}
                >
                  Apply Default
                </Button>
                {prefixOptions.length > 0 && (
                  <Checkbox.Group
                    label="Case prefixes"
                    value={filters.prefixKeys ?? []}
                    onChange={handlePrefixKeysChange}
                  >
                    <Group gap="xs" mt={4}>
                      {prefixOptions.map((prefixKey) => (
                        <Checkbox
                          key={prefixKey}
                          value={prefixKey}
                          label={
                            prefixKey === "__OTHER__"
                              ? "Other / nonstandard number"
                              : prefixKey
                          }
                          size="xs"
                        />
                      ))}
                    </Group>
                  </Checkbox.Group>
                )}
                {prefixOptionsError && (
                  <Alert
                    color="red"
                    variant="light"
                    role="alert"
                    title="Prefixes unavailable"
                  >
                    The prefix choices could not be loaded.
                  </Alert>
                )}
                <Text size="xs" c="dimmed">
                  Defaults exclude patient selections and free-text search.
                </Text>
                {(defaultError ||
                  defaultLoadError ||
                  (rawDefault && !savedDefault)) && (
                  <Alert
                    color="red"
                    variant="light"
                    role="alert"
                    title="Default unavailable"
                  >
                    {defaultError ??
                      "The saved default could not be loaded or is no longer valid."}
                  </Alert>
                )}
                {defaultMessage && (
                  <Text size="xs" role="status">
                    {defaultMessage}
                  </Text>
                )}
                {activeFilters && (
                  <ActiveFilters
                    filters={filters}
                    prefixOptions={prefixOptions}
                    onFiltersChange={handleActiveFiltersChange}
                    onClearAll={handleClearFilters}
                  />
                )}
              </>
            </Group>
          </Paper>
        </Group>

        <QueueCaseTable
          cases={activeCases}
          pagination={activePagination}
          loading={activeLoading}
          filtered={activeFilters}
          columns={columns}
          sortState={sorts}
          onSort={handleSort}
          onPageChange={handleRevealMoreCases}
          onClearFilters={handleClearFilters}
          renderHeader={renderHeader}
          renderRow={renderRow}
        />
        {(activeError || blockedError) && (
          <Alert
            color="red"
            variant="light"
            role="alert"
            title="Could not load cases"
          >
            The case list could not be loaded. Please refresh and try again.
          </Alert>
        )}
        {!activeLoading && activeCases.length > 0 && (
          <Stack gap="xs" align="center">
            <Text size="sm" c="dimmed" role="status" aria-live="polite">
              {hasMoreCases
                ? `Showing ${activeCases.length} of ${activeTotal} cases`
                : `All ${activeCases.length} cases shown`}
            </Text>
            {hasMoreCases && (
              <>
                <Box ref={attachLoadBoundary} h={1} aria-hidden="true" />
                <Button
                  size="sm"
                  variant="light"
                  onClick={handleRevealMoreCases}
                  loading={activeFetchingNextPage}
                >
                  Load more cases
                </Button>
              </>
            )}
          </Stack>
        )}

        {(blockedTotal > 0 || blockedLoading) && (
          <>
            <QueueCaseTable
              cases={blockedCases}
              pagination={blockedPagination}
              loading={blockedLoading}
              filtered={activeFilters}
              blocked
              totalCount={blockedTotal}
              columns={columns}
              sortState={sorts}
              onSort={handleSort}
              onPageChange={handleRevealMoreBlockedCases}
              onClearFilters={handleClearFilters}
              renderHeader={renderHeader}
              renderRow={renderRow}
            />
            {!blockedLoading && blockedCases.length > 0 && (
              <Stack gap="xs" align="center">
                <Text size="sm" c="dimmed" role="status" aria-live="polite">
                  {hasMoreBlockedCases
                    ? `Showing ${blockedCases.length} of ${blockedTotal} blocked cases`
                    : `All ${blockedCases.length} blocked cases shown`}
                </Text>
                {hasMoreBlockedCases && (
                  <>
                    <Box
                      ref={attachBlockedLoadBoundary}
                      h={1}
                      aria-hidden="true"
                    />
                    <Button
                      size="sm"
                      variant="light"
                      onClick={handleRevealMoreBlockedCases}
                      loading={blockedFetchingNextPage}
                    >
                      Load more blocked cases
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>

      <FilterModal
        opened={filterModalOpened}
        onClose={handleCloseFilters}
        filters={filters}
        onApply={handleApplyFilters}
        allowedStatusNames={[
          "Pending Pathologist Review",
          "Pathologist Review",
          "Pending External Report",
          "Completed",
        ]}
        pathologistFilterMode={tab === "sentOut" ? "assigned-user" : "editable"}
        prefixOptions={prefixOptions}
        showQueueFilters
      />
    </Tabs.Panel>
  );
}
